<template>
  <div class="settings-page">
    <h2>People</h2>
    <div class="subtitle">Manage your organization members and invites.</div>

    <div class="people-list">
      <div v-for="member in members" :key="member.id" class="person-row">
        <div class="person-info">
          <Avatar
            :personish="{
              kind: 'attendee',
              email: member.email,
              name: member.name,
            }"
            size="small"
            badge-side="hide"
          />
          <div class="details">
            <div class="name">{{ member.name }}</div>
            <div class="email">{{ member.email }}</div>
          </div>
        </div>
        <div class="status">
          <span class="role-badge">{{ roleLabel(member) }}</span>
          <ButtonMenu :offset="[0, 4]" placement="bottom-end">
            <template #btn>
              <BaseButton kind="auxiliary">
                <template #icon>
                  <MoreIcon />
                </template>
              </BaseButton>
            </template>
            <template #content>
              <BaseButton
                kind="auxiliary"
                text="Remove Member"
                is-expanded
                :state="canRemoveMember(member) ? 'default' : 'disabled'"
                :tooltip="
                  canRemoveMember(member)
                    ? undefined
                    : 'You are not allowed to perform this action.'
                "
                :tooltip-display="canRemoveMember(member) ? 'none' : 'always'"
                @click="removeMember(member.id)"
              >
                <template #icon>
                  <DeleteIcon />
                </template>
              </BaseButton>
            </template>
          </ButtonMenu>
        </div>
      </div>
    </div>

    <div v-if="pendingInvites?.length" class="pending-invites-section">
      <h3>Pending invites</h3>
      <div class="pending-list">
        <div v-for="email in pendingInvites" :key="email" class="person-row">
          <div class="person-info">
            <Avatar
              :personish="{
                kind: 'attendee',
                email: email,
                name: email.split('@')[0],
              }"
              size="small"
              badge-side="hide"
            />
            <div class="details">
              <div class="name">{{ email.split("@")[0] }}</div>
              <div class="email">{{ email }}</div>
            </div>
          </div>
          <div class="status">
            <span class="pending-badge">Pending</span>
            <ButtonMenu :offset="[0, 4]" placement="bottom-end">
              <template #btn>
                <BaseButton kind="auxiliary">
                  <template #icon>
                    <MoreIcon />
                  </template>
                </BaseButton>
              </template>
              <template #content>
                <BaseButton
                  kind="auxiliary"
                  text="Cancel Invite"
                  is-expanded
                  @click="cancelInvite(email)"
                >
                  <template #icon>
                    <DeleteIcon />
                  </template>
                </BaseButton>
              </template>
            </ButtonMenu>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canInviteMember" class="invite-section">
      <h3>Invite people</h3>
      <p class="info-text">
        By default, people invited to the organization will join the default
        workspace. You will need to add them to other workspaces manually.
      </p>

      <div class="invite-input">
        <FormInput
          v-model:value="inviteEmails"
          type="text"
          placeholder="Emails, comma separated"
          :state="
            errorInEmails ? 'error' : canInviteMember ? 'default' : 'disabled'
          "
          @keydown.enter="canInviteMember && inviteUsers()"
        />
        <BaseButton
          kind="main"
          type="primary"
          text="Invite"
          :state="canInviteMember ? 'default' : 'disabled'"
          :tooltip="
            canInviteMember
              ? undefined
              : 'You are not allowed to perform this action.'
          "
          :tooltip-display="canInviteMember ? 'none' : 'always'"
          @click="inviteUsers"
        />
      </div>
      <BaseAlert v-if="errorInEmails" state="error">
        Email format is incorrect, please fix it and try again.
      </BaseAlert>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs } from "vue";

import { AccountId, OrganizationMemberFull } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import Avatar from "@/components/elements/Avatar.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import * as inject from "@/components/inject";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

export default defineComponent({
  name: "People",
  components: {
    Avatar,
    BaseAlert,
    BaseButton,
    ButtonMenu,
    DeleteIcon,
    FormInput,
    MoreIcon,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { orgId } = toRefs(props);
    const controller = inject.controller();
    const inviteEmails = ref("");
    const errorInEmails = ref(false);
    const showMoreOptions = ref(true);

    const releaser = useReleaser();
    const [organization, members, pendingInvites] = await (async () => {
      const orgRef = releaser.add(
        bindAsync(orgId, async (id) =>
          id ? controller.organization.get(id) : releasableRef(null)
        )
      );
      const memberIds = releaser.add(
        bindAsync(orgId, async (id) =>
          id ? controller.organization.member.all(id) : releasableRef([])
        )
      );
      const members = releaser.add(
        memberIds.then((ids) =>
          bindAsync(ids, async (ids) => {
            if (!ids?.length)
              return releasableRef<OrganizationMemberFull[]>([]);

            const memberPromises = ids.map(async (memberId: AccountId) => {
              const memberRef = await controller.organization.member.get(
                orgId.value!,
                memberId
              );
              const member = memberRef.value;
              memberRef.release();
              if (!member) return null;
              return member;
            });

            const loadedMembers = await Promise.all(memberPromises);
            return releasableRef(
              loadedMembers.filter(
                (
                  member: OrganizationMemberFull | null
                ): member is OrganizationMemberFull => member !== null
              )
            );
          })
        )
      );

      const pendingInvites = releaser.add(
        bindAsync(orgId, async (id) =>
          id ? controller.organization.member.pending(id) : releasableRef([])
        )
      );

      return Promise.all([orgRef, members, pendingInvites]);
    })();

    const emailRegexp =
      /^([^<>()[\]\\,;:\s@"]+)@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/;
    const inviteUsers = async () => {
      const emails = inviteEmails.value.split(",").map((email) => email.trim());
      if (emails.some((email) => !email.match(emailRegexp))) {
        errorInEmails.value = true;
      } else {
        errorInEmails.value = false;

        if (orgId.value) {
          inviteEmails.value = "";
          await Promise.all(
            emails.map(async (email) => {
              if (email) {
                await controller.organization.member.invite(
                  orgId.value!,
                  email
                );
              }
            })
          );
        }
      }
    };

    const removeMember = async (memberId: string) => {
      if (orgId.value) {
        await controller.organization.member.remove(orgId.value, memberId);
      }
    };

    const cancelInvite = async (email: string) => {
      if (orgId.value) {
        await controller.organization.member.uninvite(orgId.value, email);
      }
    };

    const canInviteMember = computed(() => {
      return (
        organization.value?.permissions?.includes("member-invite") ?? false
      );
    });

    const canRemoveMember = (member: OrganizationMemberFull) => {
      return member.permissions?.includes("remove") ?? false;
    };

    const roleLabel = (member: OrganizationMemberFull) =>
      member.access_role === "owner" ? "Owner" : "Member";

    return {
      members,
      pendingInvites,
      inviteEmails,
      errorInEmails,
      showMoreOptions,
      canInviteMember,
      canRemoveMember,
      roleLabel,
      inviteUsers,
      removeMember,
      cancelInvite,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss" as *;

.people-list {
  margin: 0 0 24px 0;
}

.pending-invites-section {
  margin: 24px 0;
}

.pending-list {
  margin: 0 0 8px 0;
}

.person-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid;
  @include themed() {
    border-color: t($grey-10);
  }

  &:last-child {
    border-bottom: none;
  }
}

.person-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.details {
  .name {
    @extend %font-body-medium;
    @include themed() {
      color: t($primary);
    }
  }

  .email {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}

.status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.role-badge {
  padding: 2px 6px;
  border-radius: $border-radius-s;
  @extend %font-caption-regular;
  @include themed() {
    background-color: t($grey-10);
    color: t($grey-60);
  }
}

.pending-badge {
  padding: 2px 6px;
  border-radius: $border-radius-l;
  @extend %font-caption-regular;
  @include themed() {
    border: 1px solid t($tertiary-50);
    color: t($tertiary-50);
  }
}

.invite-section {
  margin-bottom: 24px;
  margin-top: 16px;
}

.info-text {
  margin-bottom: 16px;
  @include themed() {
    color: t($grey-60);
  }
}

.invite-input {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  :deep(.form-input) {
    flex: 1;
  }

  .base-button {
    flex-shrink: 0;
  }
}

.error {
  color: var(--red);
  font-size: 14px;
  margin-top: 4px;
}
</style>
